import type { ReactNode } from "react";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";
import { useTheme } from "@/lib/ground";
import { kitEdge } from "@/lib/team-color";
import { cn } from "@/lib/utils";

/**
 * The phone half of every organiser list.
 *
 * A data table does not survive 375px (columns crush and names wrap
 * mid-word), so below the `adm:` breakpoint every list becomes rows where the
 * WHOLE row is the tap target. There is no separate Edit button to aim at,
 * which matters when the aiming is done with a thumb in the sun.
 *
 * A row that is shown but cannot be acted on stays visible and reads as
 * disabled rather than disappearing: somebody who cannot see their game
 * assumes they are finished.
 */
export function RecordList({ children, className }: { children: ReactNode; className?: string }) {
  return <ItemGroup className={cn("gap-2", className)}>{children}</ItemGroup>;
}

export function RecordRow({
  lead,
  title,
  description,
  note,
  wrap = false,
  result,
  actions,
  selected = false,
  disabled = false,
  featured = false,
  onClick,
  className,
}: {
  /** Time, rank, jersey number: the narrow left column. */
  lead?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** A second description line. Its own line because `description` truncates
   *  and the state note ("Opponent not decided yet") is what got cut. */
  note?: ReactNode;
  /**
   * Let the title use as many lines as it needs.
   *
   * For a row whose title is TWO things of equal weight — a matchup, where
   * truncating the second team is the same as not showing it. Off everywhere
   * else: a list of names reads faster when every row is one line high.
   */
  wrap?: boolean | undefined;
  /** Right-hand value: a score, a total, a chevron. */
  result?: ReactNode;
  /** At most TWO controls. A third wraps at 375px: put it in the row body. */
  actions?: ReactNode;
  selected?: boolean | undefined;
  disabled?: boolean | undefined;
  /** Marks the featured / next-up row with the pink jersey stripe. */
  featured?: boolean | undefined;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
}) {
  const body = (
    <>
      {lead !== undefined && (
        <span
          data-lead=""
          className="tabular w-12 shrink-0 self-start pt-0.5 text-xs font-semibold text-muted-foreground"
        >
          {lead}
        </span>
      )}
      <ItemContent className="min-w-0">
        {/* `ItemTitle` ships `w-fit`, `flex` and `line-clamp-1` from shadcn.
            `w-fit` is why a long title never ellipsed: the box grew to the
            text instead of to the row, so the `overflow:hidden` had nothing to
            clip and the name ran straight past the card edge. `w-full
            min-w-0` is what makes truncation actually happen.

            The wrapping branch is a plain div rather than an `ItemTitle` with
            more classes on it: `line-clamp-none`, `block` and `flex` all set
            `display`, and which one wins is decided by Tailwind's own utility
            order, not by the order they are written in. Not a fight worth
            having over one row. */}
        {wrap ? (
          <div className="text-base leading-snug font-medium text-pretty">{title}</div>
        ) : (
          <ItemTitle className="w-full min-w-0 truncate text-base font-medium">{title}</ItemTitle>
        )}
        {description !== undefined && (
          <ItemDescription className="line-clamp-2 text-pretty">{description}</ItemDescription>
        )}
        {note !== undefined && (
          <ItemDescription className="line-clamp-2 text-pretty">{note}</ItemDescription>
        )}
      </ItemContent>
      {result !== undefined && (
        <span className="tabular shrink-0 text-base font-semibold">{result}</span>
      )}
      {actions !== undefined && (
        // ACTIONS TAKE THEIR OWN ROW ON A PHONE. `Item` already wraps, but the
        // content column is `flex-1 min-w-0`, so it collapsed to nothing rather
        // than pushing the buttons down: a name crushed to "Горв…" next to a
        // pair of buttons that kept their full width. `w-full` makes the wrap
        // happen, and the buttons then split the row instead of hugging left.
        <ItemActions className="w-full shrink-0 sm:w-auto [&>*]:flex-1 sm:[&>*]:flex-none">
          {actions}
        </ItemActions>
      )}
    </>
  );

  const shell = cn(
    "bg-card text-left",
    // Selected reads as solid ink, never pink: pink marks brand and featured
    // things, not "this one is chosen".
    selected
      ? // The lead (a start time) too: muted grey on solid ink measured 3.26:1.
        "border-primary bg-primary text-primary-foreground [&_[data-lead]]:text-primary-foreground/70 [&_[data-slot=item-description]]:text-primary-foreground/70"
      : "border-border",
    // The stripe FOLLOWS the card's radius: no `rounded-l-none` here. Squaring
    // the left corners drew a straight pink bar down a card whose other three
    // corners curve, and the mismatch is what the eye catches — the 3px border
    // curving into the corner with everything else reads as part of the card.
    featured && !selected && "stripe",
    disabled && "border-dashed bg-transparent opacity-55",
    className,
  );

  // A row with no action is not a button: giving it one announces a control
  // that does nothing to anybody navigating by them.
  if (onClick === undefined) {
    return (
      <Item variant="outline" className={shell}>
        {body}
      </Item>
    );
  }

  return (
    <Item
      variant="outline"
      className={cn(shell, !disabled && !selected && "hover:bg-accent")}
      // `aria-current` rather than `aria-pressed`: a picked row in a list of
      // choices is the current one, and a toggle's "not pressed" would be read
      // out on every other row.
      render={
        <button
          type="button"
          disabled={disabled}
          aria-current={selected ? "true" : undefined}
          onClick={onClick}
        />
      }
    >
      {body}
    </Item>
  );
}

/**
 * Connection state, in a word plus a dot. Ok / error / warn / idle only: a
 * fifth state means somebody is using this to say something it should say in
 * a sentence.
 */
export function Dot({
  tone,
  label,
  pulse = false,
  className,
}: {
  tone: "ok" | "err" | "warn" | "idle";
  label: string;
  pulse?: boolean | undefined;
  className?: string | undefined;
}) {
  const fill =
    tone === "ok"
      ? "bg-go"
      : tone === "err"
        ? "bg-stop"
        : tone === "warn"
          ? "bg-warn"
          : "bg-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 text-xs font-medium whitespace-nowrap",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn("size-2 shrink-0 rounded-full", fill, pulse && "animate-pulse")}
      />
      {label}
    </span>
  );
}

/**
 * A team's colour, as a disc. Team colours are arbitrary user content, so this
 * is a fill and never a text colour: anything set ON a team colour has to go
 * through `readableOn` (lib/team-color.ts).
 *
 * `data-edge` is the kit's call per theme (`kitEdge`): a white kit is no dot
 * at all on the light theme's white card, so under 3:1 against the pane the
 * disc wears a hairline ring in the row's own ink (`.kit-edge`, theme.css).
 */
export function ColorDot({ color, className }: { color: string; className?: string }) {
  const theme = useTheme();
  return (
    <span
      aria-hidden="true"
      data-edge={kitEdge(color, theme)}
      className={cn("kit-edge size-2.5 shrink-0 rounded-full", className)}
      style={{ background: color }}
    />
  );
}
